import { Input, List, Modal, Spin, Typography } from 'antd';
import React from 'react';
import { PricingAPI } from '../../../../../controller/PricingAPI';
import TodoItem from './TodoItem';
import { GROUP_RENDER_LIMIT, isOccurrence, notifyTodosChanged, showRequestError } from '../todoHelpers';

// 「待我處理」是快速執行的視角，不傳 onNoteEdit：編輯既有工作摘要屬於細節操作，
// 留在「已忽略」「已完成」頁籤 (管理員另有「全部」)，TodoItem 在沒有 onNoteEdit
// 時不會顯示編輯連結。
//
// 範圍是「指派給我的，加上沒有負責人且屬於我部門 (或沒有部門) 的無主工作」，
// 依錨點分成逾期／今天／明天／後天／之後。無主工作直接混在日期分組裡，
// 任何人都能直接完成或略過，不必先認領。
// 件數為 0 的段落仍顯示標題 - 「明天沒事」本身是有用的資訊，隱藏會讓人
// 分不出是沒事還是壞了。
function TodoAgenda({ refreshTime, onEdit, onChanged, focusId = null }) {
    const [groups, setGroups] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [acting, setActing] = React.useState(false);
    // 與清單頁籤同形的完成 Modal：「待我處理」是主要操作面，完成時一樣要留工作摘要
    const [completeModal, setCompleteModal] = React.useState({ open: false, todoId: null });
    const [note, setNote] = React.useState('');
    // 哪些段落被展開了。預設都收起，只渲染前 GROUP_RENDER_LIMIT 筆
    const [expanded, setExpanded] = React.useState({});

    // 切片只套在真實待辦上，虛擬的例行項目一律渲染。
    // 排序把整天的真實待辦排在整天的虛擬項目之前，而正式站「今天」有 144 筆
    // 整天的無主工作 - 一刀切前 20 筆會把今天的例行工作整個推到展開之後，
    // 而「開始」按鈕只在虛擬項目上。虛擬項目的數量上限是範本數，全部渲染
    // 不會失控 (與後端那個上限同一個理由)。
    const visibleItems = (items) => {
        let shown = 0;
        return items.filter((item) => {
            if (isOccurrence(item)) {
                return true;
            }
            shown += 1;
            return shown <= GROUP_RENDER_LIMIT;
        });
    };

    const hiddenCount = (items) => Math.max(0, items.filter((item) => !isOccurrence(item)).length - GROUP_RENDER_LIMIT);

    // 從信件連結進來、在這一頁把它完成之後，它就不在 agenda 裡了 (agenda 只有未完成的)。
    // 那時說「不存在」是錯的 - 它就是剛剛那個人做完的。記住看過它一次就夠，
    // 不必再打一次 API (而且 API 也查不到：scopeVisibleTo 的無主與部門兩支都要求 state = new)
    //
    // 記的是「看過的那個 id」而不是布林：重設 ref 不會觸發重繪，布林會在換 id 的
    // 那一次 render 讀到上一筆的答案 (對新的 id 說「已經完成了」)。記 id 的話，
    // 換 id 時同一次 render 比對就是對的，不必等 effect
    const focusSeenId = React.useRef(null);
    // 只捲一次，之後重抓不要把使用者的滾動位置搶回去
    const focusScrolled = React.useRef(false);
    const focusRowRef = React.useRef(null);

    React.useEffect(() => {
        focusScrolled.current = false;
    }, [focusId]);

    // focusId 落在某段的第 20 筆之後時，不展開那一段就看不到它
    React.useEffect(() => {
        if (focusId == null) {
            return;
        }
        const hit = groups.find((g) => g.items.some((item) => Number(item.id) === Number(focusId)));
        if (hit) {
            focusSeenId.current = String(focusId);
            setExpanded((prev) => (prev[hit.key] ? prev : { ...prev, [hit.key]: true }));
        }
    }, [focusId, groups]);

    // 段落展開後那一列才會掛上來，所以也要跟著 expanded 重跑
    React.useEffect(() => {
        if (focusId == null || focusScrolled.current || !focusRowRef.current) {
            return;
        }
        // jsdom 沒有實作 scrollIntoView
        if (typeof focusRowRef.current.scrollIntoView === 'function') {
            focusRowRef.current.scrollIntoView({ block: 'center' });
            focusScrolled.current = true;
        }
    }, [focusId, groups, expanded]);

    const refresh = React.useCallback(() => {
        setLoading(true);
        PricingAPI.todos.agenda()
            .then((response) => setGroups(response.data.groups))
            .catch(showRequestError)
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => {
        refresh();
    }, [refresh, refreshTime]);

    const act = React.useCallback((promise) => {
        setActing(true);
        promise.then(() => {
            notifyTodosChanged();
            // onChanged 會讓父層遞增 refreshTime，觸發本元件的 effect 重抓；
            // 有它就只靠它，沒有才自己重抓，避免一次動作送兩個請求。
            if (onChanged) {
                onChanged();
            } else {
                refresh();
            }
        }).catch((error) => {
            showRequestError(error);
            refresh();
        }).finally(() => setActing(false));
    }, [refresh, onChanged]);

    const hideCompleteModal = () => setCompleteModal({ open: false, todoId: null });
    const handleCompleteOk = () => {
        act(PricingAPI.todos.updateState(completeModal.todoId, { state: 'completed', note }));
        hideCompleteModal();
    };

    const isEmpty = groups.length > 0 && groups.every((g) => g.items.length === 0);

    // 信件連結可能指向已刪除、已改派到別部門、或我沒有權限看到的待辦。
    // 看不到與不存在在這個專案一律同樣處置，所以訊息也只有一種說法
    const focusMissing = focusId != null
        && groups.length > 0
        && !groups.some((g) => g.items.some((item) => Number(item.id) === Number(focusId)));

    return (
        <Spin spinning={loading}>
            {focusMissing ? (
                <Typography.Paragraph type="warning" style={{ marginBottom: 16 }}>
                    {focusSeenId.current === String(focusId)
                        ? '這筆工作已經完成了'
                        : '這筆工作不在你的待處理清單裡 - 可能已經有人完成，或是已經被刪除'}
                </Typography.Paragraph>
            ) : null}
            {isEmpty ? (
                <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
                    目前沒有待你處理的工作。
                </Typography.Paragraph>
            ) : null}
            {groups.map((group) => (
                <div key={group.key} style={{ marginBottom: 20 }}>
                    <Typography.Title
                        level={5}
                        type={group.key === 'overdue' && group.items.length > 0 ? 'danger' : undefined}
                        style={{ marginBottom: 8 }}
                    >
                        {`${group.label} (${group.items.length})`}
                        {group.date ? <Typography.Text type="secondary" style={{ marginLeft: 8, fontWeight: 'normal' }}>{group.date}</Typography.Text> : null}
                    </Typography.Title>
                    {group.items.length === 0 ? (
                        <Typography.Text type="secondary">沒有排程的工作</Typography.Text>
                    ) : (
                        <>
                        <List
                            dataSource={expanded[group.key] ? group.items : visibleItems(group.items)}
                            // 虛擬項目沒有 id，用 (範本, 日期) 當 key；
                            // 不給 rowKey 會退回索引，重抓後順序一變就會錯位
                            rowKey={(item) => (isOccurrence(item) ? `t${item.template_id}-${item.occurrence_date}` : item.id)}
                            renderItem={(item) => (
                                <List.Item style={{ padding: '3px 0' }}>
                                    <TodoItem
                                        item={item}
                                        defaultExpanded={focusId != null && Number(focusId) === Number(item.id)}
                                        highlighted={focusId != null && Number(focusId) === Number(item.id)}
                                        rowRef={focusId != null && Number(focusId) === Number(item.id) ? focusRowRef : null}
                                        loading={acting}
                                        onEdit={onEdit}
                                        onCompleted={(id) => { setNote(''); setCompleteModal({ open: true, todoId: id }); }}
                                        onIgnored={(id) => act(PricingAPI.todos.updateState(id, { state: 'ignored' }))}
                                        onUndo={(id) => act(PricingAPI.todos.updateState(id, { state: 'new' }))}
                                        onClaim={(id) => act(PricingAPI.todos.claim(id))}
                                        onRelease={(id) => act(PricingAPI.todos.release(id))}
                                        onStart={(occ) => act(PricingAPI.todos.occurrences.store({
                                            template_id: occ.template_id,
                                            date: occ.occurrence_date,
                                        }))}
                                        onItemsChanged={() => { if (onChanged) { onChanged(); } else { refresh(); } }}
                                    />
                                </List.Item>
                            )}
                        />
                        {!expanded[group.key] && hiddenCount(group.items) > 0 ? (
                            <Typography.Link onClick={() => setExpanded((prev) => ({ ...prev, [group.key]: true }))}>
                                {`還有 ${hiddenCount(group.items)} 筆，展開`}
                            </Typography.Link>
                        ) : null}
                        </>
                    )}
                    {group.truncated ? (
                        <Typography.Text type="warning">{Number.isInteger(group.limit) ? `這一段超過 ${group.limit} 筆，只顯示前 ${group.limit} 筆` : '這一段的工作太多，只顯示前面一部分'}</Typography.Text>
                    ) : null}
                </div>
            ))}
            <Modal title="完成待辦" visible={completeModal.open} onOk={handleCompleteOk} onCancel={hideCompleteModal} okText="送出" cancelText="取消">
                <Typography>請輸入工作摘要</Typography>
                <br />
                <Input.TextArea rows={5} placeholder="請輸入工作摘要" value={note} onChange={(e) => setNote(e.target.value)} />
            </Modal>
        </Spin>
    );
}

export default TodoAgenda;
